import { styled } from '@mui/material';

export const Section = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '128px',
  width: '100%',

  [theme.breakpoints.down('2xl')]: {
    gap: '96px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '32px',
  },
}));

export const Hero = styled('section')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

export const TableBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '72px',
  width: '100%',

  [theme.breakpoints.down('lg')]: {
    gap: '48px',
  },
  [theme.breakpoints.down('md')]: {
    gap: '40px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '32px',
  },
}));

export const Table = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
});

export const HeaderRow = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns:
    'minmax(140px, 1.15fr) minmax(220px, 2fr) repeat(4, minmax(72px, 1fr))',
  gap: '16px',
  alignItems: 'center',
  padding: '0 44px',
  marginBottom: '28px',
  color: theme.palette.color.fg.primary,
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',

  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

export const HeaderCell = styled('span')({
  '&:not(:first-of-type):not(:nth-of-type(2))': {
    textAlign: 'center',
  },
});

export const Row = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns:
    'minmax(140px, 1.15fr) minmax(220px, 2fr) repeat(4, minmax(72px, 1fr))',
  alignItems: 'center',
  padding: '44px',
  backgroundColor: theme.palette.color.global.card,
  borderRadius: '24px',
  color: theme.palette.color.fg.primary,
  fontSize: '22px',
  fontWeight: 600,
  lineHeight: 1.4,

  [theme.breakpoints.down('lg')]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    gap: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
  },
}));

export const RowCell = styled('div', {
  shouldForwardProp: (prop) => !['align', 'hasDivider'].includes(prop),
})(({ theme, align = 'flex-start', hasDivider = false }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: align,
  height: '100%',
  minHeight: '44px',
  padding: '0 22px',

  '&:first-of-type': {
    paddingLeft: 0,
  },
  '&:last-of-type': {
    paddingRight: 0,
  },
  ...(hasDivider && {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      right: 0,
      width: '1px',
      height: '42px',
      backgroundColor: theme.palette.color.stroke.dividerTransparent,
      transform: 'translateY(-50%)',
    },
  }),

  [theme.breakpoints.down('lg')]: {
    justifyContent: 'center',
    width: '100%',
    padding: 0,

    '&::after': {
      display: 'none',
    },
  },
}));

export const Logo = styled('img')(({ theme }) => ({
  display: 'block',
  width: 'auto',
  maxWidth: '100%',
  height: '36px',
  objectFit: 'contain',
  objectPosition: 'left center',

  [theme.breakpoints.down('lg')]: {
    height: '50px',
    margin: '0 auto',
    objectPosition: 'center',
  },
}));

export const Purpose = styled('p')(({ theme }) => ({
  margin: 0,

  [theme.breakpoints.down('lg')]: {
    textAlign: 'center',
  },
}));

export const FeatureIcon = styled('img')(({ theme }) => ({
  display: 'block',
  width: '44px',
  height: '44px',

  [theme.breakpoints.down('sm')]: {
    width: '23px',
    height: '23px',
  },
}));

export const FeatureRowCell = styled(RowCell)(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

export const MobileFeatureList = styled('div')(({ theme }) => ({
  display: 'none',
  flexDirection: 'column',
  width: '100%',

  [theme.breakpoints.down('lg')]: {
    display: 'flex',
  },
}));

export const MobileFeatureItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '12px 0',
  fontSize: '16px',
  fontWeight: 500,
  color: theme.palette.color.fg.tetriary,
  textTransform: 'uppercase',

  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.color.stroke.dividerTransparent}`,
  },
}));

export const VippsButton = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  width: 'fit-content',
  minWidth: '320px',
  height: '64px',
  padding: '0 32px',
  border: 'none',
  borderRadius: '120px',
  backgroundColor: '#ff5b24',
  color: theme.palette.color.fg.primary,
  fontSize: '22px',
  fontWeight: 600,
  lineHeight: 1,
  cursor: 'pointer',
  outline: 'none',
  textDecoration: 'none',
  transition: 'transform 0.2s ease-in-out, opacity 0.2s ease-in-out',

  '&:hover': {
    opacity: 0.9,
  },
  '&:active': {
    transform: 'scale(0.98)',
  },

  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
    height: '56px',
    fontSize: '18px',
    padding: '0 24px',
  },
}));

export const VippsLogo = styled('img')({
  display: 'block',
  width: '64px',
  height: '16px',
});
